import {fileRenamedOnly} from '@github-ui/diff-file-helpers'
import {Diff, type DiffProps} from '@github-ui/diff-lines'
import {DiffFeature} from '@github-ui/diffs/types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {removeUrlHash} from '@github-ui/history'
import {memo, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import type {ContextLineRange} from '../page-data/loaders/use-context-lines-data'
import {
  useDiffSummary,
  useCollapsedDiffStatus,
  useCollapsedDiffStatusActions,
  useDiffEntryActions,
  useComparisonInfo,
  useMarkers,
  useGetSelectedMarkerId,
  useSelectedCommentId,
  useActiveChangeGroup,
  usePullRequestBasePath,
  useCombinedCurrentUser,
  useCommentsPreference,
  useLineSpacingPreference,
  useCopilotDiffContext,
} from '../stores/selectors'
import {publishAddCopilotChatReference, publishOpenCopilotChat} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotChatPanelID, copilotDockedPanelID} from '@github-ui/copilot-chat/utils/constants'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useCopilotChatReference} from '@github-ui/diff-lines/hooks/use-copilot-chat-reference'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCodeowners, getPathOwnership} from '../page-data/loaders/use-codeowners-data'
import {CodeownersBadge, getCodeownersText} from './CodeownersBadge'
import {BlobActionsMenu} from './BlobActionsMenu'
import {MarkAsViewedButton} from './MarkAsViewedButton'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {MINI_HEADER_HEIGHT, STICKY_HEADER_HEIGHT} from '../helpers/constants'
import {fileLevelCommentKey, type DiffLine} from '@github-ui/diff-lines/types'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import {getSelectedFullDiffHash} from '@github-ui/diffs/document-hash-helpers'
import {IconButton} from '@primer/react'
import {CommentIcon} from '@primer/octicons-react'
import {useRichDiffQueryConfig} from '../page-data/loaders/use-rich-diff-page-data'
import {contextRangesForFile} from '../utils/context-ranges-for-markers'
import {clsx} from 'clsx'
import responsiveStyles from './responsive.module.css'
import type {Group} from '../types/group-types'
import {scopeToDiffLinesByGroup} from '../utils/scope-to-diff-lines-by-group'
import {estimateDiffSize} from '../helpers/estimate-diff-size'

function focusActiveElement(activeElement?: HTMLElement) {
  let cellId: string | null = null
  let filePath: string | null = null

  if (activeElement) {
    if (activeElement.hasAttribute('data-grid-cell-id')) {
      cellId = activeElement.getAttribute('data-grid-cell-id')
    }
    if (activeElement.hasAttribute('data-file-path')) {
      filePath = activeElement.getAttribute('data-file-path')
    }
  }

  setTimeout(() => {
    if (cellId) {
      const cell = document.querySelector<HTMLElement>(`[data-grid-cell-id="${CSS.escape(cellId)}"]`)
      if (cell) {
        cell.focus()
        return
      }
    }
    if (filePath) {
      const button = document.querySelector<HTMLElement>(`button[data-file-path="${CSS.escape(filePath)}"]`)
      if (button) {
        button.focus()
      }
    }
  }, 0)
}

export type PullRequestDiffProps = Omit<
  DiffProps,
  | 'addInjectedContextLines'
  | 'currentUser'
  | 'leftSideContent'
  | 'rightSideContent'
  | 'onToggleCollapse'
  | 'initialExpandedMarkerId'
  | 'fileComments'
  | 'isAddingFileComment'
  | 'fileComments'
  | 'setIsAddingFileComment'
  | 'collapsed'
> & {
  activeGroup?: Group | undefined
  diffLines: DiffLine[]
  estimatedDiffHeight?: number // in pixels, for content visibility optimization
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
}

// Access data store, maps threads into the difflines
function PullRequestDiffUnmemoized({
  diffManuallyExpanded,
  headBranchName,
  headRepoName,
  headRepoOwner,
  diffLines,
  path,
  isSelected = false,
  reviewed,
  ...diff
}: PullRequestDiffProps) {
  const basePath = usePullRequestBasePath()
  const activeGroup = useActiveChangeGroup()
  const {fetchContextLines} = useDiffEntryActions()
  const comparisonInfo = useComparisonInfo()
  const w = comparisonInfo.hideWhitespace ? '1' : '0'
  const currentUser = useCombinedCurrentUser()
  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()
  const richDiffQueryConfig = useRichDiffQueryConfig({
    basePath,
    path,
  })
  const diffRef = useRef<HTMLDivElement>(null)
  const addFileCommentButtonRef = useRef<HTMLButtonElement>(null)
  const {helpUrl} = useAppPayload<PullRequestAppPayload>()
  const isCollapsed = useCollapsedDiffStatus(path)
  const {updateAllFilesCollapsedStatus, updateFileCollapsedStatus} = useCollapsedDiffStatusActions()

  const getSelectedMarkerId = useGetSelectedMarkerId()

  const [isAddingFileComment, setIsAddingFileComment] = useState(false)

  // Rich diff state management
  const [showRichDiff, setShowRichDiff] = useState(diff.richDiff?.defaultToRichDiff ?? false)

  const selectedCommentId = useSelectedCommentId()

  // When this file is selected via comment navigation, force the diff lines
  // to be unhidden
  const shouldForceExpandForComment = isSelected && !!selectedCommentId

  const diffSummary = useDiffSummary(path)
  const estimatedDiffHeight: number | undefined = useMemo(() => {
    return estimateDiffSize({
      diffSummary,
      commentPreference,
      lineSpacingPreference,
      isCollapsed,
    })
  }, [commentPreference, lineSpacingPreference, diffSummary, isCollapsed])

  const threadsAndAnnotations = useMarkers(path)

  // Get codeowners data for responsive menu
  const codeownersQuery = useCodeowners({basePath})
  const codeownersData = getPathOwnership({diffPath: path, codeownersData: codeownersQuery?.data})

  const fileComments = useMemo(() => {
    if (!threadsAndAnnotations) return []

    return threadsAndAnnotations[fileLevelCommentKey]?.threads ?? []
  }, [threadsAndAnnotations])

  const copilotDiffContextData = useCopilotDiffContext()
  const copilotDiffContext = useMemo(() => {
    if (!copilotDiffContextData) return undefined

    return {
      commitMessage: copilotDiffContextData.commitMessage,
      pullRequestUrl: copilotDiffContextData.pullRequestUrl,
    }
  }, [copilotDiffContextData])

  const copilotChatReference = useCopilotChatReference({
    ...diff,
    path,
    hasCopilotAccess: currentUser.hasCopilotAccess,
    commitMessage: copilotDiffContext?.commitMessage,
    pullRequestUrl: copilotDiffContext?.pullRequestUrl,
  })

  const handleAskAboutDiff = useCallback(() => {
    if (!copilotChatReference) return
    const chatPanelIsOpen =
      !!document.getElementById(copilotChatPanelID) || !!document.getElementById(copilotDockedPanelID)
    if (chatPanelIsOpen) {
      sendEvent('copilot.file-diff.attach', {target: 'file-diff-header', mode: 'assistive', action: 'add_reference'})
      publishAddCopilotChatReference(copilotChatReference)
    } else {
      sendEvent('copilot.file-diff.attach', {target: 'file-diff-header', mode: 'assistive', action: 'new_thread'})
      publishOpenCopilotChat({
        intent: CopilotChatIntents.conversation,
        references: [copilotChatReference],
        newThread: true,
        skipImplicitReferences: true,
        id: 'file-diff-header',
      })
    }
  }, [copilotChatReference])

  const scopedDiffLines: DiffLine[] = useMemo(() => {
    if (!activeGroup) return diffLines

    return scopeToDiffLinesByGroup(diffLines, activeGroup, path)
  }, [activeGroup, path, diffLines])

  const expandDiff = useCallback(() => {
    updateFileCollapsedStatus(path, false)
  }, [path, updateFileCollapsedStatus])

  /**
   * HASH LOGIC
   *
   * Setting this value ensures that when navigating directly to a marker (comment or annotation) via a URL hash,
   * the thread containing that comment is expanded, even when the user is in "Comments Minimized" mode.
   * Works in tandem with the `DiffLineTableCellParts` logic to enter dialog mode and the `ReviewThreadComment`
   *  to focus the comment.
   *
   * The value changes whenever the URL hash changes.
   */

  // Used to determine if the hash logic code allows for a diff to be expanded or not.
  const canExpandDiffOnSelectionRef = useRef(true)

  const updateExpandedMarkerFromUrlHash = useCallback(() => {
    const initialExpandedMarkerId = getSelectedMarkerId()

    if (initialExpandedMarkerId && isCollapsed) {
      const containsMarker =
        threadsAndAnnotations &&
        Object.values(threadsAndAnnotations).some(
          line =>
            line.threads.some(t => String(t.id) === String(initialExpandedMarkerId)) ||
            line.annotations.some(a => String(a.id) === String(initialExpandedMarkerId)),
        )

      if (containsMarker) {
        expandDiff()
      }
    }
  }, [isCollapsed, expandDiff, getSelectedMarkerId, threadsAndAnnotations])

  const clearUrlHash = useCallback(() => {
    removeUrlHash()
    updateExpandedMarkerFromUrlHash()
  }, [updateExpandedMarkerFromUrlHash])

  /**
   * Expand on hash change logic.
   *
   * This logic is used to expand the diff when the hash changes to a specific diff entry.
   * This ensures that navigation to a specific diff entry via URL hash always expands the diff.
   * Scenarios include navigation from the file tree, comments, or other parts of the UI.
   */
  const expandDiffWhenSelected = useCallback(() => {
    const diffHash = getSelectedFullDiffHash()

    if (diffHash === diff.pathDigest && isCollapsed) {
      expandDiff()
    }
  }, [diff.pathDigest, expandDiff, isCollapsed])

  // consolidated hash change handler to reduce number of event listeners
  const handleHashChanges = useEffectEvent(() => {
    updateExpandedMarkerFromUrlHash()
    expandDiffWhenSelected()
    canExpandDiffOnSelectionRef.current = false
  })

  useEffect(() => {
    const handler = () => handleHashChanges()
    handler()
    ssrSafeWindow?.addEventListener('hashchange', handler)
    return () => {
      ssrSafeWindow?.removeEventListener('hashchange', handler)
    }
  }, [])

  // Ensure that the diff is expanded if the isSelected prop has changed to true
  useEffect(() => {
    // Reset ref state to true when diff becomes unselected
    if (!isSelected) {
      canExpandDiffOnSelectionRef.current = true
      return
    }

    // Early return as diff is unable to expand on selection
    if (!canExpandDiffOnSelectionRef.current) {
      return
    }

    expandDiffWhenSelected()
    canExpandDiffOnSelectionRef.current = false
  }, [expandDiffWhenSelected, isSelected])
  /**
   * END HASH LOGIC
   */
  const onToggleCollapse = useCallback(
    (willCollapse: boolean, filePath: string, event?: React.MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event && event.altKey) {
        updateAllFilesCollapsedStatus(willCollapse)
      } else {
        updateFileCollapsedStatus(filePath, willCollapse)
        if (!willCollapse) return
        ssrSafeWindow?.requestAnimationFrame(() => {
          const rect = diffRef.current?.getBoundingClientRect()

          if (!rect) return

          // Only the top boundary is checked to ensure the element is not obscured by the sticky header or scrolled
          // out of view for when collapsed while sticky. We don't need to check the bottom because all we care about
          // is the diff header being visible
          const stickyHeight =
            STICKY_HEADER_HEIGHT + (isFeatureEnabled('updated_pull_request_header') ? MINI_HEADER_HEIGHT : 0)
          const isOutOfView = rect.top < stickyHeight

          if (!isOutOfView) return
          ssrSafeWindow?.scrollTo({
            top: rect.top + ssrSafeWindow.scrollY - stickyHeight,
          })
        })
      }
    },
    [updateAllFilesCollapsedStatus, updateFileCollapsedStatus],
  )
  const expandedRangesArray = useRef<ContextLineRange[]>(
    contextRangesForFile({
      diffSummary,
    }),
  )
  const [hasExpandedAllRanges, setHasExpandedAllRanges] = useState(false)

  const addInjectedContextLines = useCallback(
    async (range: ContextLineRange) => {
      // When the diff re-renders, we don't want to scroll to the targeted
      // line or comment as we would on the initial render.
      clearUrlHash()

      expandedRangesArray.current.push(range)
      fetchContextLines({
        contextLineRanges: expandedRangesArray.current,
        path,
        w,
      })
    },
    [clearUrlHash, fetchContextLines, path, w],
  )

  const loadDiff = useCallback(async () => {
    await fetchContextLines({
      contextLineRanges: [],
      path,
    })
  }, [fetchContextLines, path])

  // When navigating to a comment in this file, switch to source diff
  // so inline comments can render, and load diff lines if they haven't
  // been fetched yet. Only runs for the selected diff.
  useEffect(() => {
    if (shouldForceExpandForComment) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowRichDiff(false)

      if (diffLines.length === 0) {
        void loadDiff()
      }
    }
  }, [shouldForceExpandForComment, diffLines.length, loadDiff])

  const canExpandOrCollapseLines = useMemo(() => {
    if (diff.isBinary || diff.isSubmodule || diff.isTooBig) return false

    if (
      fileRenamedOnly({
        linesChanged: diff.linesChanged,
        newTreeEntry: diff.newTreeEntry,
        oldTreeEntry: diff.oldTreeEntry,
        status: diff.status,
        truncatedReason: diff.truncatedReason,
      })
    )
      return false

    if (hasExpandedAllRanges) return true
    if (scopedDiffLines.length === 0) return false
    const firstDiffLineNumber = scopedDiffLines[1]?.blobLineNumber || 0

    if (firstDiffLineNumber > 1) return true

    const initialDiffLineCount = scopedDiffLines.length
    const lastDiffLineNumber = scopedDiffLines[initialDiffLineCount - 1]?.blobLineNumber || 0
    const fileLineCount = diff.newTreeEntry?.lineCount || 0

    //- 1 on the diff line count because there is always the top hunk diff line no matter what
    if (lastDiffLineNumber < fileLineCount || initialDiffLineCount - 1 < fileLineCount) return true

    return false
  }, [
    diff.isBinary,
    diff.isSubmodule,
    diff.isTooBig,
    diff.linesChanged,
    diff.newTreeEntry,
    diff.oldTreeEntry,
    diff.status,
    diff.truncatedReason,
    scopedDiffLines,
    hasExpandedAllRanges,
  ])

  const expandAllContextLines = useCallback(async () => {
    expandedRangesArray.current = []
    if (!hasExpandedAllRanges) {
      expandedRangesArray.current.push({
        start: 1,
        end: Math.max(diff.oldTreeEntry?.lineCount ?? 0, diff.newTreeEntry?.lineCount ?? 0),
      })
    }

    const activeElement = document.activeElement as HTMLElement

    await fetchContextLines({
      contextLineRanges: expandedRangesArray.current,
      path,
      w,
    })
    setHasExpandedAllRanges(prev => !prev)

    if (activeElement) {
      focusActiveElement(activeElement)
    }
  }, [hasExpandedAllRanges, diff.oldTreeEntry?.lineCount, diff.newTreeEntry?.lineCount, fetchContextLines, path, w])

  const onToggleRichDiff = useCallback(() => setShowRichDiff(prev => !prev), [])

  const richDiffToggle = useMemo(
    () =>
      diff.richDiff?.canToggleRichDiff
        ? {
            richDiff: diff.richDiff,
            isRichDiff: showRichDiff,
            onToggle: onToggleRichDiff,
          }
        : undefined,
    [diff.richDiff, showRichDiff, onToggleRichDiff],
  )

  const codeownersText = useMemo(
    () =>
      codeownersData && (codeownersData.isOwnedByViewer || codeownersData.owners.length > 0)
        ? getCodeownersText(codeownersData.isOwnedByViewer, codeownersData.owners, undefined, currentUser.login)
        : undefined,
    [codeownersData, currentUser.login],
  )

  const codeownersUrl = codeownersData?.ruleUrl

  const onCommentOnFile = currentUser.canComment ? () => setIsAddingFileComment(true) : undefined

  const onCopyPath = useCallback(() => navigator.clipboard?.writeText(path), [path])

  const onExpandLines = canExpandOrCollapseLines ? expandAllContextLines : undefined

  const richDiffState = useMemo(
    () => ({
      richDiff: diff.richDiff,
      showRichDiff,
      onToggleRichDiff: setShowRichDiff,
      queryConfig: richDiffQueryConfig,
    }),
    [diff.richDiff, showRichDiff, richDiffQueryConfig],
  )

  const hiddenFeatures = useMemo(() => {
    // TODO: Combine Pull Request Specific features to this set stored in `PullRequestStore`
    const features = new Set<DiffFeature>([])
    if (activeGroup) {
      features.add(DiffFeature.diffStats).add(DiffFeature.expandLines)
    }

    if (comparisonInfo.fullDiff.headOid !== comparisonInfo.effectiveRange.headOid) {
      features.add(DiffFeature.markAsViewed)
    }

    return features
  }, [activeGroup, comparisonInfo.effectiveRange.headOid, comparisonInfo.fullDiff.headOid])

  const {disabledFeatures, disabledReasons} = useMemo(() => {
    const features = new Set<DiffFeature>([])
    const reasons = new Map<DiffFeature, string>()
    if (activeGroup) {
      features.add(DiffFeature.markAsViewed)
      reasons.set(DiffFeature.markAsViewed, 'This file can only be marked as viewed on the “All files” page')
    }

    return {disabledFeatures: features, disabledReasons: reasons}
  }, [activeGroup])

  return (
    <Diff
      // keeping this property above the spread in order to keep a test passing, this is not
      // a change in functionality from what is currently deployed in prod
      loadDiff={loadDiff}
      // if you have any properties explicitly set above the spreading of `...diff`, and they are present on the
      // diff object itself, then the version present in the diff object will override the explicitly set version.
      // as such, I am moving the spread to the very top so that anything explicitly set will take precedence.
      {...diff}
      copilotDiffContext={copilotDiffContext}
      currentUser={currentUser}
      addFileCommentButtonRef={addFileCommentButtonRef}
      diffManuallyExpanded={diffManuallyExpanded || shouldForceExpandForComment}
      estimatedDiffHeight={estimatedDiffHeight}
      fileComments={fileComments}
      hiddenFeatures={hiddenFeatures}
      isAddingFileComment={isAddingFileComment}
      isSelected={isSelected}
      leftSideContent={
        <CodeownersBadge
          className={clsx('mx-1', responsiveStyles['hide-on-mobile'])}
          diffPath={path}
          pullRequestBasePath={basePath}
          viewerLogin={currentUser.login}
        />
      }
      rightSideContent={
        <div className="d-flex flex-items-center gap-2">
          {!hiddenFeatures.has(DiffFeature.markAsViewed) && (
            <MarkAsViewedButton
              path={path}
              basePath={basePath}
              inactive={disabledFeatures.has(DiffFeature.markAsViewed) || !!activeGroup}
              inactiveTooltip={disabledReasons.get(DiffFeature.markAsViewed)}
              setIsCollapsed={value => onToggleCollapse(value, path)}
            />
          )}
          {currentUser.canComment && (
            <IconButton
              ref={addFileCommentButtonRef}
              icon={CommentIcon}
              aria-label="Comment on this file"
              aria-expanded={isAddingFileComment}
              variant="invisible"
              size="small"
              onClick={event => {
                // Expand the file first so the comment UI becomes available.
                if (isCollapsed) {
                  onToggleCollapse(false, path)
                }

                setIsAddingFileComment(true)

                event.stopPropagation()
              }}
              className={responsiveStyles['hide-on-mobile']}
            />
          )}
          <BlobActionsMenu
            oid={diff.status === 'REMOVED' && diff.oldCommitOid ? diff.oldCommitOid : diff.newCommitOid || ''}
            path={path}
            repo={{
              name: headRepoName || diff.repository.name,
              ownerLogin: headRepoOwner || diff.repository.ownerLogin,
            }}
            isViewable={!diff.isSubmodule}
            isDeleteable={!diff.isSubmodule}
            isEditable={!diff.isSubmodule}
            shouldShowEditOptions={currentUser.shouldDisplayEditOptions}
            branchName={headBranchName}
            returnToPullRequestPath={basePath}
            onCommentOnFile={onCommentOnFile}
            onCopyPath={onCopyPath}
            onExpandLines={onExpandLines}
            areLinesExpanded={hasExpandedAllRanges}
            richDiffToggle={richDiffToggle}
            codeownersText={codeownersText}
            codeownersUrl={codeownersUrl}
            onAskAboutDiff={
              copilotFeatureFlags.prChatEnhancements && copilotChatReference ? handleAskAboutDiff : undefined
            }
          />
        </div>
      }
      canExpandOrCollapseLines={canExpandOrCollapseLines}
      expandAllContextLines={expandAllContextLines}
      hasExpandedAllRanges={hasExpandedAllRanges}
      addInjectedContextLines={addInjectedContextLines}
      onToggleCollapse={(ev, value) => onToggleCollapse(value, path, ev)}
      path={path}
      ref={diffRef}
      setIsAddingFileComment={setIsAddingFileComment}
      collapsed={isCollapsed}
      helpUrl={helpUrl}
      linesAdded={diffSummary?.linesAdded || diff.linesAdded || 0}
      linesChanged={diffSummary?.linesChanged || diff.linesChanged || 0}
      linesDeleted={diffSummary?.linesDeleted || diff.linesDeleted || 0}
      diffLines={scopedDiffLines}
      richDiffState={richDiffState}
    />
  )
}

export const PullRequestDiff = memo(PullRequestDiffUnmemoized)
